import React, { useState } from 'react';
import { ShoppingCart, Package, ExternalLink, Settings2, Star, RefreshCw, Globe, Lock } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';
import CheckoutModal from './CheckoutModal';
import PlacementEditor from './PlacementEditor';

const ProductDisplay = ({ products, onCheckout, onProductUpdate }) => {
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productImageIndex, setProductImageIndex] = useState({}); // Track current mockup index per product
  const [settingDefaultMockup, setSettingDefaultMockup] = useState(new Set()); // Track products being updated
  const [productDefaults, setProductDefaults] = useState({}); // Track default mockup index per product
  const [togglingVisibility, setTogglingVisibility] = useState(new Set()); // Track products having visibility toggled
  const [productVisibility, setProductVisibility] = useState({}); // Track visibility state per product

  // Get all available mockup images for a product
  const getProductMockups = (product) => {
    if (!product.images || !Array.isArray(product.images)) {
      return [];
    }
    return product.images.map(img => img.src).filter(Boolean);
  };

  // Get current image based on mockup index
  const getCurrentImage = (product) => {
    const mockups = getProductMockups(product);
    const currentIndex = productImageIndex[product.id] || 0;
    if (mockups.length > 0 && currentIndex < mockups.length) {
      return mockups[currentIndex];
    }
    return product.mockupUrl || '/api/placeholder/300/400';
  };

  // Change mockup view for a product
  const handleMockupChange = (productId, index) => {
    setProductImageIndex(prev => ({
      ...prev,
      [productId]: index
    }));
  };

  // Set current mockup as default for this product
  const handleSetDefaultMockup = async (productId, mockupIndex) => {
    setSettingDefaultMockup(prev => new Set([...prev, productId]));
    try {
      const response = await amplifyApi.setDefaultMockup(productId, mockupIndex);
      if (response.success) {
        // Update local tracking of defaults
        setProductDefaults(prev => ({
          ...prev,
          [productId]: mockupIndex
        }));
        // Notify parent if callback provided
        if (onProductUpdate) {
          const product = products.find(p => p.id === productId);
          if (product) {
            onProductUpdate({ ...product, defaultMockupIndex: mockupIndex });
          }
        }
      } else {
        console.error('Failed to set default mockup:', response.error);
      }
    } catch (err) {
      console.error('Failed to set default mockup:', err);
    } finally {
      setSettingDefaultMockup(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // Toggle product visibility (public/private)
  const handleToggleVisibility = async (product) => {
    const productId = product.id;
    setTogglingVisibility(prev => new Set([...prev, productId]));

    try {
      // Get current visibility (from local state or product data)
      const currentVisibility = productVisibility[productId] ?? product.visibility ?? 'private';
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';

      const response = await amplifyApi.updateProductVisibility(productId, newVisibility);

      if (response.success) {
        // Update local tracking
        setProductVisibility(prev => ({
          ...prev,
          [productId]: newVisibility
        }));
        // Notify parent if callback provided
        if (onProductUpdate) {
          onProductUpdate({ ...product, visibility: newVisibility });
        }
      } else {
        console.error('Failed to update visibility:', response.error);
        alert('Failed to update visibility. Please try again.');
      }
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
      alert('Failed to update visibility. Please try again.');
    } finally {
      setTogglingVisibility(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleBuyNow = (product) => {
    setCheckoutProduct(product);
    setIsCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setIsCheckoutOpen(false);
    setCheckoutProduct(null);
  };

  const handleOrderComplete = (order) => {
    alert(`Order #${order.external_id} created successfully! Total: $${order.total}`);
    setIsCheckoutOpen(false);
    setCheckoutProduct(null);
  };

  const handleEditPlacement = (product) => {
    setEditingProduct(product);
  };

  const handlePlacementSave = (updatedProduct) => {
    setEditingProduct(null);
    if (onProductUpdate) {
      onProductUpdate(updatedProduct);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => {
          const mockups = getProductMockups(product);
          const currentIndex = productImageIndex[product.id] || 0;
          const hasMultipleMockups = mockups.length > 1;
          const defaultIndex = productDefaults[product.id] ?? (product.defaultMockupIndex || 0);
          const isCurrentDefault = currentIndex === defaultIndex;
          const currentVisibility = productVisibility[product.id] ?? product.visibility ?? 'private';
          const isPublic = currentVisibility === 'public';
          const isTogglingVisibility = togglingVisibility.has(product.id);

          return (
          <div key={product.id} className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-1">
            <div className="relative aspect-[3/4] bg-slate-100 overflow-hidden">
              <img
                src={getCurrentImage(product)}
                alt={product.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
              {/* Mockup navigation indicator */}
              {hasMultipleMockups && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
                  {/* Clickable counter - cycles through mockups */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newIndex = currentIndex === mockups.length - 1 ? 0 : currentIndex + 1;
                      handleMockupChange(product.id, newIndex);
                    }}
                    className="flex items-center gap-1.5 bg-black/40 hover:bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full transition-colors cursor-pointer"
                    title="Click to see next view"
                  >
                    <span className="text-white text-[11px] font-medium">
                      {currentIndex + 1}/{mockups.length}
                    </span>
                    {isCurrentDefault && (
                      <Star size={10} className="text-yellow-400 fill-yellow-400" />
                    )}
                  </button>

                  {/* Set as default button - only show on non-default views */}
                  {!isCurrentDefault && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefaultMockup(product.id, currentIndex);
                      }}
                      disabled={settingDefaultMockup.has(product.id)}
                      className="flex items-center gap-1 bg-yellow-500/90 hover:bg-yellow-500 text-white text-[11px] font-medium px-2 py-1 rounded-full backdrop-blur-sm transition-colors disabled:opacity-50"
                      title="Set as default image"
                    >
                      {settingDefaultMockup.has(product.id) ? (
                        <RefreshCw size={10} className="animate-spin" />
                      ) : (
                        <Star size={10} />
                      )}
                    </button>
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 backdrop-blur-[2px]">
                <button
                  className="bg-white text-slate-900 px-4 py-2.5 rounded-full font-bold transform translate-y-4 group-hover:translate-y-0 transition-all duration-200 hover:bg-blue-50 flex items-center gap-1.5 shadow-lg text-sm"
                  onClick={() => handleBuyNow(product)}
                >
                  <ShoppingCart size={16} />
                  Buy
                </button>
                <button
                  className="bg-white text-slate-600 px-4 py-2.5 rounded-full font-bold transform translate-y-4 group-hover:translate-y-0 transition-all duration-200 delay-75 hover:bg-slate-100 flex items-center gap-1.5 shadow-lg text-sm"
                  onClick={() => handleEditPlacement(product)}
                  title="Edit design placement"
                >
                  <Settings2 size={16} />
                  Edit
                </button>
                <button
                  className={`px-4 py-2.5 rounded-full font-bold transform translate-y-4 group-hover:translate-y-0 transition-all duration-200 delay-150 flex items-center gap-1.5 shadow-lg text-sm disabled:opacity-50 ${
                    isPublic
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={() => handleToggleVisibility(product)}
                  disabled={isTogglingVisibility}
                  title={isPublic ? 'Make private' : 'Make public'}
                >
                  {isTogglingVisibility ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : isPublic ? (
                    <Globe size={16} />
                  ) : (
                    <Lock size={16} />
                  )}
                  {isPublic ? 'Public' : 'Private'}
                </button>
              </div>
            </div>

            <div className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900 line-clamp-1" title={product.title}>
                  {product.title}
                </h3>
                <span className="font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded text-sm">
                  ${product.price}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Package size={14} />
                  <span>Premium Quality • Made to Order</span>
                </div>
                {/* Visibility indicator */}
                <div className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                  isPublic ? 'text-green-600 bg-green-50' : 'text-slate-400 bg-slate-50'
                }`}>
                  {isPublic ? <Globe size={10} /> : <Lock size={10} />}
                  <span>{isPublic ? 'Public' : 'Private'}</span>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 flex items-start gap-3">
        <ExternalLink size={20} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600">
          <strong>Secure Checkout:</strong> Complete your purchase with shipping details.
          All products are fulfilled globally by trusted print partners.
        </p>
      </div>

      <CheckoutModal
        product={checkoutProduct}
        isOpen={isCheckoutOpen}
        onClose={handleCheckoutClose}
        onOrderComplete={handleOrderComplete}
        api={amplifyApi}
      />

      {editingProduct && (
        <PlacementEditor
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handlePlacementSave}
        />
      )}
    </div>
  );
};

export default ProductDisplay;